import { Suspense, lazy, useState } from "react";
import { useTranslation } from "react-i18next";
import { Link, useSearchParams } from "react-router";

import { Button, EmptyState, ErrorState, Input, Seg, Spinner } from "@/components/ui";
import type { ComposeSeed } from "@/mail/composeSeed";
import { BLANK, draftSeed, forwardSeed, replySeed } from "@/mail/composeSeed";
import { MessageList } from "@/mail/MessageList";
import { ReadingPane } from "@/mail/ReadingPane";
import type { FolderParam } from "@/mail/queries";
import { FOLDER_PARAMS, folderFromParam, useMailbox } from "@/mail/queries";

/**
 * The composer carries the rich-text editor, which is a third of the SPA's
 * code (D41). Loaded when a seller opens it, not when they open the CRM.
 */
const MailComposer = lazy(() =>
  import("@/mail/MailComposer").then((module) => ({ default: module.MailComposer })),
);

function idFromParam(raw: string | null): number | null {
  const id = Number(raw);
  return Number.isInteger(id) && id > 0 ? id : null;
}

function pageFromParam(raw: string | null): number {
  const page = Number(raw);
  return Number.isInteger(page) && page > 0 ? page : 1;
}

/**
 * What the screen is before a mailbox exists.
 *
 * Not empty folders and a composer: every one of those would fail at the
 * server, and a seller reading "no hay correos" in an unconnected app
 * concludes their mail is missing rather than that they have one step left.
 */
function NotConnected({ needsReconnect }: { needsReconnect: boolean }) {
  const { t } = useTranslation();
  return (
    <div className="mx-auto max-w-[540px] p-8 text-center sm:p-12">
      <h1 className="text-[30px]">{t("mail.title")}</h1>
      <p className="mt-3 text-sm text-mute-600">
        {t(needsReconnect ? "mail.notConnected.reconnect" : "mail.notConnected.body")}
      </p>
      <Link className="btn btn-primary mt-5 inline-flex" to="/ajustes">
        {t("mail.notConnected.cta")}
      </Link>
    </div>
  );
}

/**
 * Recibidos, Enviados and Borradores (.docs/09 §1, D39).
 *
 * Two panes on a desktop; on a phone the list and the message share the space
 * and only one shows at a time — the same shape as the WhatsApp inbox, for the
 * same reason: half the reading happens on a phone between appointments.
 *
 * Folder, selection, search and page all live in the URL, so a message can be
 * linked to and the back button leaves the message rather than the screen.
 */
export function Correo() {
  const { t } = useTranslation();
  const [params, setParams] = useSearchParams();
  const [composing, setComposing] = useState<ComposeSeed | null>(null);
  const mailbox = useMailbox();

  const folder = folderFromParam(params.get("carpeta"));
  const selectedId = idFromParam(params.get("m"));
  const page = pageFromParam(params.get("pagina"));
  const search = params.get("q") ?? "";

  const update = (changes: Record<string, string | null>) => {
    setParams((previous) => {
      const next = new URLSearchParams(previous);
      for (const [key, value] of Object.entries(changes)) {
        if (value === null) next.delete(key);
        else next.set(key, value);
      }
      return next;
    });
  };

  const setFolder = (next: FolderParam) =>
    // The open message is not in the other folder, and a selection the list no
    // longer shows reads as a broken filter.
    update({ carpeta: next, m: null, pagina: null });

  if (mailbox.isPending) return <Spinner label={t("mail.loading")} />;
  if (mailbox.isError) {
    return (
      <ErrorState
        message={
          mailbox.error instanceof Error ? mailbox.error.message : t("errors.mailbox")
        }
        onRetry={() => void mailbox.refetch()}
      />
    );
  }
  if (mailbox.data === null || mailbox.data.status !== "connected") {
    return <NotConnected needsReconnect={mailbox.data !== null} />;
  }

  return (
    <div className="relative flex h-full min-h-0">
      <aside
        className={`flex w-full shrink-0 flex-col border-rule sm:w-80 sm:border-r-2 ${
          selectedId === null ? "flex" : "hidden sm:flex"
        }`}
      >
        <div className="space-y-2 border-b-2 border-rule p-3">
          <div className="flex items-center justify-between gap-3">
            <h1 className="text-[22px]">{t("mail.title")}</h1>
            <Button variant="primary" onClick={() => setComposing(BLANK)}>
              {t("mail.compose")}
            </Button>
          </div>
          <Seg
            name="carpeta"
            value={folder}
            onChange={setFolder}
            options={FOLDER_PARAMS.map((option) => ({
              value: option,
              label: t(`mail.folder.${option}`),
            }))}
          />
          <form
            onSubmit={(event) => {
              event.preventDefault();
              const value = new FormData(event.currentTarget).get("q");
              update({ q: String(value ?? "") || null, m: null, pagina: null });
            }}
          >
            <Input
              name="q"
              type="search"
              defaultValue={search}
              aria-label={t("mail.search")}
              placeholder={t("mail.search")}
            />
          </form>
        </div>

        <MessageList
          folder={folder}
          search={search}
          page={page}
          selectedId={selectedId}
          // A draft has nothing to read: opening one opens the composer it was
          // written in, which is also how it gets sent (D39).
          onSelect={(id) =>
            folder === "borradores"
              ? setComposing(draftSeed(id))
              : update({ m: String(id) })
          }
          onPageChange={(next) => update({ pagina: String(next), m: null })}
        />
      </aside>

      <main
        className={`min-w-0 flex-1 ${selectedId === null ? "hidden sm:block" : "block"}`}
      >
        {selectedId === null ? (
          <EmptyState>
            {t(folder === "borradores" ? "mail.pickDraft" : "mail.pickOne")}
          </EmptyState>
        ) : (
          <div className="flex h-full min-h-0 flex-col">
            <div className="border-b-2 border-rule px-3 py-1 sm:hidden">
              <button
                type="button"
                className="btn btn-ghost px-2 py-1 text-sm"
                onClick={() => update({ m: null })}
              >
                ← {t("mail.backToList")}
              </button>
            </div>
            <div className="min-h-0 flex-1">
              <ReadingPane
                messageId={selectedId}
                onReply={(message, all) => setComposing(replySeed(message, all))}
                onForward={(message) => setComposing(forwardSeed(message))}
              />
            </div>
          </div>
        )}
      </main>

      {composing ? (
        <Suspense fallback={null}>
          <MailComposer seed={composing} onClose={() => setComposing(null)} />
        </Suspense>
      ) : null}
    </div>
  );
}
